import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scanner_notifications } from '../services';
import { subHeading3, body, bodySmall, label, caption, fontWeight, spacing } from '../Constants/Fonts';
import { theme } from '../Constants/Colors';

const STORAGE_KEY = 'nmt_notification_read_ids';

// Severity badge palettes (bg / text).
const SEVERITY = {
    critical: { background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7' },
    high:     { background: 'rgba(239, 68, 68, 0.15)',  color: '#ef4444' },
    medium:   { background: 'rgba(249, 115, 22, 0.15)', color: '#f97316' },
    low:      { background: 'rgba(34, 197, 94, 0.15)',  color: '#22c55e' },
};

// Inline styles (no SCSS). Typography from Fonts.js presets + spacing scale;
// colours from the shared default theme in Colors.js so this panel matches
// the "What's New" popover exactly.
const badgeBase = {
    padding: `${spacing.xs}px ${spacing.md}px`, borderRadius: spacing.sm, letterSpacing: '0.4px',
};
const S = {
    wrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    btn: {
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        color: '#fff', width: 40, height: 40, borderRadius: '50%', position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s ease',
    },
    badge: caption({
        fontWeight: fontWeight.bold, position: 'absolute', top: 4, right: 4,
        minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8,
        background: theme.danger, color: '#fff', lineHeight: '16px', textAlign: 'center',
        boxShadow: '0 0 0 2px #05122E',
    }),
    panel: {
        position: 'absolute', top: 50, right: 0, width: 380, maxHeight: 520,
        background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: spacing.xl,
        boxShadow: theme.shadow, zIndex: 50,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${theme.border}`, background: theme.headerBg,
    },
    panelTitle: subHeading3({ fontWeight: fontWeight.bold, color: theme.titleText }),
    markAll: bodySmall({
        fontWeight: fontWeight.semibold, background: 'transparent', border: 'none',
        color: theme.primary, cursor: 'pointer', padding: `${spacing.sm}px ${spacing.md}px`,
        borderRadius: spacing.sm, transition: 'background 0.15s ease',
    }),
    panelBody: { flex: 1, overflowY: 'auto' },
    state: body({ padding: `${spacing['4xl']}px ${spacing['3xl']}px`, textAlign: 'center', color: theme.mutedText }),
    stateError: body({ padding: `${spacing['4xl']}px ${spacing['3xl']}px`, textAlign: 'center', color: theme.danger }),
    item: {
        padding: `${spacing['2xl']}px ${spacing['3xl']}px`,
        borderBottom: `1px solid ${theme.itemBorder}`, cursor: 'pointer',
        transition: 'background 0.15s ease',
    },
    meta: { display: 'flex', alignItems: 'center', gap: spacing.lg, marginBottom: spacing.md, flexWrap: 'wrap' },
    newBadge: caption({ ...badgeBase, fontWeight: fontWeight.bold, background: theme.successBg, color: theme.successText }),
    category: label({ fontWeight: fontWeight.semibold, color: theme.bodyText, textTransform: 'uppercase', letterSpacing: '0.3px' }),
    date: label({ fontWeight: fontWeight.regular, color: theme.mutedText, marginLeft: 'auto' }),
    title: body({ fontWeight: fontWeight.semibold, color: theme.titleText, marginBottom: spacing.sm, lineHeight: 1.35 }),
    desc: bodySmall({ color: theme.bodyText, lineHeight: 1.5, marginBottom: spacing.lg }),
};

const severityStyle = (sev) => {
    const s = (sev || '').toLowerCase();
    const palette = SEVERITY[s];
    if (!palette) return null;
    return caption({ ...badgeBase, fontWeight: fontWeight.bold, textTransform: 'uppercase', ...palette });
};

const readReadIds = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const writeReadIds = (ids) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
        // noop
    }
};

const formatDate = (raw) => {
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const NotificationBell = () => {
    const [open, setOpen] = useState(false);
    const [readIds, setReadIds] = useState(readReadIds);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [btnHover, setBtnHover] = useState(false);
    const [markAllHover, setMarkAllHover] = useState(false);
    const [hoveredId, setHoveredId] = useState(null);
    const wrapperRef = useRef(null);
    const navigate = useNavigate();

    const loadNotifications = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await scanner_notifications();
            // Endpoint returns { items, total, limit, offset } directly (no response_data wrapper)
            const body = res?.statusCode;
            const items = Array.isArray(body?.items)
                ? body.items
                : Array.isArray(body?.response_data?.data?.items) ? body.response_data.data.items : [];
            setNotifications(items.filter(n => !n?.dismissed_at));
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Could not load notifications.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotifications();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isItemRead = (n) => readIds.includes(String(n.id)) || !!n.read_at;
    const unreadCount = notifications.filter(n => !isItemRead(n)).length;

    const markAllRead = () => {
        const allIds = notifications.map(n => String(n.id));
        setReadIds(allIds);
        writeReadIds(allIds);
    };

    const markRead = (id) => {
        const key = String(id);
        if (readIds.includes(key)) return;
        const next = [...readIds, key];
        setReadIds(next);
        writeReadIds(next);
    };

    const handleAction = (n) => {
        markRead(n.id);
        if (n.link) {
            setOpen(false);
            navigate(n.link);
        }
    };

    return (
        <div style={S.wrapper} ref={wrapperRef}>
            <button
                type="button"
                style={{ ...S.btn, background: btnHover ? 'rgba(255, 255, 255, 0.08)' : 'transparent' }}
                onClick={() => setOpen(prev => !prev)}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                aria-label="Notifications"
            >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {unreadCount > 0 && (
                    <span style={S.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
            </button>

            {open && (
                <div style={S.panel}>
                    <div style={S.header}>
                        <span style={S.panelTitle}>Notifications</span>
                        <button
                            type="button"
                            style={{
                                ...S.markAll,
                                ...(unreadCount === 0 ? { color: '#475569', cursor: 'not-allowed' } : {}),
                                ...(markAllHover && unreadCount > 0 ? { background: 'rgba(59, 130, 246, 0.1)' } : {}),
                            }}
                            onClick={markAllRead}
                            disabled={unreadCount === 0}
                            onMouseEnter={() => setMarkAllHover(true)}
                            onMouseLeave={() => setMarkAllHover(false)}
                        >
                            Mark all as read
                        </button>
                    </div>

                    <div style={S.panelBody}>
                        {loading ? (
                            <div style={S.state}>Loading…</div>
                        ) : error ? (
                            <div style={S.stateError}>{error}</div>
                        ) : notifications.length === 0 ? (
                            <div style={S.state}>No notifications</div>
                        ) : (
                            notifications.map(n => {
                                const isUnread = !isItemRead(n);
                                const sevStyle = severityStyle(n.severity);
                                return (
                                    <div
                                        key={n.id}
                                        style={{
                                            ...S.item,
                                            background: hoveredId === n.id ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
                                        }}
                                        onMouseEnter={() => setHoveredId(n.id)}
                                        onMouseLeave={() => setHoveredId(null)}
                                        onClick={() => (n.link ? handleAction(n) : markRead(n.id))}
                                    >
                                        <div style={S.meta}>
                                            {isUnread && <span style={S.newBadge}>NEW</span>}
                                            {n.severity && sevStyle && <span style={sevStyle}>{n.severity}</span>}
                                            {n.category && <span style={S.category}>{n.category}</span>}
                                            {n.created_at && <span style={S.date}>{formatDate(n.created_at)}</span>}
                                        </div>
                                        {n.title && <div style={S.title}>{n.title}</div>}
                                        {n.body && <div style={S.desc}>{n.body}</div>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
